<template>
  <div class="left-nav">
    <span
      class="column-title"
      :style="{
        backgroundColor: navPageLeftArea.titleBgColor,
        color: navPageLeftArea.titleTextColor,
      }"
      >{{ curSiblingColumnInfo.label }}</span
    >
    <a
      v-for="(item, index) in curSiblingColumnInfo.children"
      :key="index"
      :class="'column-item' + (item.id == columnId ? ' current' : '')"
      :style="
        item.id == columnId
          ? {
              backgroundColor: navPageLeftArea.activeBgColor,
              color: navPageLeftArea.activeTextColor,
            }
          : null
      "
      :href="item.data.hrefURL ? item.data.hrefURL : 'javascript:void(0);'"
    >
      <svg
        class="icon"
        width="8"
        height="8"
        viewBox="0 0 1024 1024"
        :fill="item.id == columnId ? navPageLeftArea.activeTextColor : null"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M950.857 512L73.143 1023.999V0z"></path>
      </svg>
      <span>{{ item.label }}</span>
    </a>
  </div>
</template>

<script setup>
// #region 引用
import useSSRInitialStateStore from '@/stores/ssr-state';
import { useSSRContext } from '@/ssr/use-context';
// #endregion

// #region props/emit

// #endregion

// #region 变量/常量
const ssrInitialState = useSSRInitialStateStore();
const curSiblingColumnInfo = ref(null); // 当前同级栏目列表
const columnId = ref(null); // 当前栏目id
const navPageLeftArea = ref(null); // 左侧导航区域数据

// #endregion

// #region 业务方法

// SSR 期间同步读取数据（父组件 onServerPrefetch 已设置）
if (import.meta.env.SSR) {
  const ssrContext = useSSRContext();
  curSiblingColumnInfo.value =
    ssrContext.initialState?.data?.current?.curSiblingColumnInfo;
  columnId.value = ssrContext.initialState?.data?.current?.columnId;
  navPageLeftArea.value =
    ssrContext.initialState?.data?.current?.topNavComponent?.navPageLeftArea;
} else {
  // 客户端从initialState读取数据
  curSiblingColumnInfo.value =
    ssrInitialState.data.current.curSiblingColumnInfo;
  columnId.value = ssrInitialState.data.current.columnId;
  navPageLeftArea.value =
    ssrInitialState.data.current.topNavComponent.navPageLeftArea;
}

// #endregion
</script>

<style scoped lang="scss">
@import '@/styles/website/_variables.scss';

// ============================================
// 左侧导航组件变量（继承官网设计系统）
// ============================================
$leftnav-color-primary: $website-primary;          // 主色调（深蓝）
$leftnav-color-accent: $website-primary-light;     // 强调色（亮青）
$leftnav-color-text-dark: $website-text-primary;   // 深色文字
$leftnav-color-text-muted: $website-text-muted;     // 弱化文字
$leftnav-color-border: $website-border-light;       // 边框色

// 动画时长
$leftnav-duration-fast: $website-duration-fast;
$leftnav-duration-normal: $website-duration-normal;

// 动画曲线
$leftnav-ease-out: $website-ease-out;

/* PC端样式 */
@include pc {
  .left-nav {
    width: 100%;
    background: $website-bg-primary;
    border-radius: $website-radius-md;
    box-shadow: $website-shadow-md;
    overflow: hidden;
  }

  // ========== 标题样式 ==========
  .left-nav .column-title {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    font-size: 16px;
    font-weight: 600;
    color: #fff;
    background-color: $leftnav-color-primary;
    letter-spacing: 0.5px;

    // 底部装饰线
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 20%;
      right: 20%;
      height: 2px;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    }
  }

  // ========== 菜单项样式 ==========
  .left-nav .column-item {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 60px;
    padding: 0 20px;
    font-size: 16px;
    color: $leftnav-color-text-dark;
    background-color: #fff;
    border-bottom: 1px solid $leftnav-color-border;
    transition: all $leftnav-duration-normal $leftnav-ease-out;
    cursor: pointer;

    // 图标样式
    .icon {
      width: 8px;
      height: 8px;
      margin-right: 12px;
      transition: transform $leftnav-duration-fast $leftnav-ease-out;
    }

    // 文字样式
    span {
      flex: 1;
      text-align: left;
    }

    // 左侧边框（hover时显示）
    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 60%;
      border-radius: 0 $website-radius-sm $website-radius-sm 0;
      transition: width $leftnav-duration-normal $leftnav-ease-out;
    }

    // ========== hover 状态 ==========
    &:hover {
      background-color: rgba($leftnav-color-primary, 0.05);
      color: $leftnav-color-primary;

      .icon {
        transform: translateX(3px);  // 图标右移
      }

      // 显示左侧边框
      &::after {
        width: 3px;
        background: $leftnav-color-primary;
      }
    }

    // ========== 激活状态 ==========
    &.current {
      color: $leftnav-color-accent;
      // 渐变背景（从左到右渐隐）
      background: linear-gradient(90deg,
        rgba($leftnav-color-accent, 0.1) 0%,
        rgba($leftnav-color-accent, 0.05) 50%,
        rgba($leftnav-color-accent, 0) 100%
      );
      font-weight: 500;

      // 左侧装饰条
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 30px;
        background: linear-gradient(180deg, $leftnav-color-primary, $leftnav-color-accent);
        border-radius: 0 $website-radius-sm $website-radius-sm 0;
      }

      // 图标旋转效果
      .icon {
        transform: rotate(90deg);
      }
    }

    // 最后一项去掉底部边框
    &:last-child {
      border-bottom: none;
    }
  }
}

/* 移动端样式 */
@include mobile {
  .left-nav {
    display: none;  // 移动端隐藏
  }
}
</style>
